import type { ReactNode } from "react"
import { ArrowLeft } from "lucide-react"
import { Link } from "@tanstack/react-router"

import { cn } from "@/lib/utils"

/**
 * The band every ATS page opens with: a title, a line saying what the page is,
 * and whatever that page does on the right.
 *
 * It exists because the pages had drifted — some drew a header on the chrome
 * with a hairline under it, some let the title float on the page ground with
 * nothing behind it, and the two read as different products side by side. One
 * component means a page cannot get it wrong by omission.
 *
 * Chrome is white in light and near-black in dark, and it separates from the
 * page rather than from a card: 19k draws the top bar #FFFFFF over a #F4F5F9
 * screen, and this band is the same surface one level down.
 */
export function PageHeader({
  title,
  /** One line on what the page is for. Hidden on narrow, where the count says more. */
  description,
  /** A machine value narrow shows in place of the description — "34 records". */
  meta,
  /**
   * A page reached from another one — a set of results under the search that
   * produced it. The results page drew its own header for want of this, which
   * left the one page in the ATS whose title scrolled away with the body.
   */
  back,
  actions,
  children,
  className,
}: {
  title: string
  description?: string
  meta?: string
  back?: { to: string; label: string }
  /** The page's own actions, right-aligned on the title's line. */
  actions?: ReactNode
  /** Tabs or filters that belong to the header rather than the body. */
  children?: ReactNode
  className?: string
}) {
  return (
    <header
      className={cn(
        "shrink-0 border-b bg-chrome px-4 pb-3 pt-3 @2xl:px-[26px] @2xl:pb-4 @2xl:pt-5",
        className,
      )}
    >
      <div className="flex items-start gap-3.5">
        {back && (
          <Link
            to={back.to}
            aria-label={back.label}
            className="mt-1 flex size-7 flex-none items-center justify-center rounded-md border text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
          </Link>
        )}
        <div className="min-w-0">
          {/*
            One line. Most page titles are a word, but a title can be a saved
            search's own prompt — a sentence — and a chrome band that grows to
            hold it pushes the page down by however much someone typed.
          */}
          <h1 className="truncate text-[14px] font-medium leading-[1.2] @2xl:text-2xl @2xl:font-semibold @2xl:leading-[1.1] @2xl:tracking-[-0.025em]">
            {title}
          </h1>
          {/*
            A machine fact about the page. It steps aside above @2xl only when
            there is a description to take the line — a page whose header says
            nothing else keeps its count at every width, which is what the
            results page wants: how many are on screen is the fact about it.
          */}
          {meta && (
            <p
              className={cn(
                "mt-[3px] font-mono text-[11px] leading-[1.2] text-muted-foreground @2xl:mt-1.5 @2xl:text-[11.5px]",
                description && "@2xl:hidden",
              )}
            >
              {meta}
            </p>
          )}
          {description && (
            <p
              className={cn(
                "mt-1.5 text-[13px] leading-[1.5] text-muted-foreground",
                meta && "hidden @2xl:block",
              )}
            >
              {description}
            </p>
          )}
        </div>

        {actions && (
          <div className="ml-auto flex flex-none items-center gap-[18px]">
            {actions}
          </div>
        )}
      </div>

      {children}
    </header>
  )
}

/**
 * A page action: a quiet word, not a filled pill.
 *
 * The canvas is consistent about this across every screen it draws — actions
 * are text at the weight of the page, and the one filled control in the product
 * is "New conversation" in Robin's rail.
 */
/**
 * An action on a page's title line.
 *
 * Three tones, and the choice between them is about rank rather than taste:
 *
 * - `primary` — the one thing the page is for. Canvas turn 33 draws it filled,
 *   on Tasks, on the calendar and on Team Settings alike, so the product's
 *   primary create is a pill and there is exactly one of them per page.
 * - `teal` — an action that matters but is not why you came.
 * - `quiet` — everything else. Export CSV is the type case.
 *
 * Before turn 33 the primary action was a teal word and the only filled control
 * in the product was Robin's "New conversation". That rule is retired here: the
 * pill is easier to find on a page whose content is a wall of records, and a
 * page with one obvious way to add something reads better than a row of equal
 * words.
 */
export function PageAction({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: "primary" | "teal" | "quiet"
}) {
  const { tone = "quiet", ...rest } = props
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        tone === "primary" &&
          "h-9 flex-none gap-2 rounded-md bg-primary px-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90",
        tone === "teal" && "font-medium text-brand-ink hover:opacity-80",
        tone === "quiet" && "text-muted-foreground hover:text-foreground",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
